import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Check, KeyRound, Loader2, Plus, Search, Shield, Trash2, X } from "lucide-react";
import { base_url } from "../../config";

type Role = {
  id: number;
  name: string;
  label: string;
  is_admin: boolean;
  protected: boolean;
  users: number;
  permissions: string[];
};

type PermissionRow = {
  id: number;
  name: string;
  label: string;
  /** The admin tab this permission opens, or null if it gates something else. */
  opens: string | null;
  roles: string[];
  direct_users: number;
};

type ManagedUser = {
  id: number;
  name: string;
  email: string;
  role: string;
  direct_permissions: string[];
};

const TABS = [
  { id: "roles", name: "Roles" },
  { id: "permissions", name: "Permissions" },
];

const api = (path: string) => `${base_url}/api/admin/${path}`;

/** Pull the server's complaint out of a 422 rather than showing "Request failed". */
const errorText = (error: any, fallback: string) =>
  error?.response?.data?.message ||
  Object.values(error?.response?.data?.errors || {})
    .flat()
    .join(" ") ||
  fallback;

const Permissions = () => {
  const [tab, setTab] = useState("roles");
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<PermissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [r, p] = await Promise.all([axios.get(api("roles")), axios.get(api("permissions"))]);
      setRoles(r.data || []);
      setPermissions(p.data || []);
    } catch (e) {
      setError(errorText(e, "Could not load roles and permissions."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Every mutation goes through here so the two tabs never disagree about the data. */
  const mutate = async (path: string, payload: any, success: string) => {
    setError("");
    setNotice("");
    try {
      await axios.post(api(path), payload);
      await load();
      setNotice(success);
      return true;
    } catch (e) {
      setError(errorText(e, "That did not work."));
      return false;
    }
  };

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto max-w-[1500px] px-4 py-5 space-y-4">
        <header className="flex items-center gap-2">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-400" />
            Permissions
          </h1>
        </header>

        <div className="flex items-center gap-1.5 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                tab === t.id
                  ? "bg-blue-500/25 text-blue-100 border-blue-400/50"
                  : "bg-slate-800/60 text-slate-300 border-slate-700/50 hover:border-slate-600/70"
              }`}
            >
              {t.name}
            </button>
          ))}
        </div>

        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-100">
            {error}
          </div>
        )}
        {notice && (
          <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-100">
            {notice}
          </div>
        )}

        {loading ? (
          <div className="text-sm text-slate-400">Loading…</div>
        ) : tab === "roles" ? (
          <RolesTab roles={roles} permissions={permissions} mutate={mutate} />
        ) : (
          <PermissionsTab roles={roles} permissions={permissions} mutate={mutate} />
        )}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------- roles

const RolesTab = ({
  roles,
  permissions,
  mutate,
}: {
  roles: Role[];
  permissions: PermissionRow[];
  mutate: (path: string, payload: any, success: string) => Promise<boolean>;
}) => {
  const [name, setName] = useState("");
  const [label, setLabel] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    const ok = await mutate("save_role", { name, label, is_admin: isAdmin }, `Role “${name}” saved.`);
    if (ok) {
      setName("");
      setLabel("");
      setIsAdmin(false);
    }
    setBusy(false);
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-400 max-w-3xl">
        A role is a named set of permissions. Users carry the role’s <em>name</em>, so it can
        only be renamed while nothing depends on it, and <code>admin</code>,{" "}
        <code>viewer</code> and <code>user</code> are named directly in the code and cannot
        be removed. “Admin area” is the coarse gate — without it the role sees no admin
        screens at all, whatever permissions it holds.
      </p>

      <div className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-3">
        <div className="text-xs font-semibold text-slate-300 mb-2">New role</div>
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Name (slug)" value={name} onChange={setName} placeholder="desk_staff" mono />
          <Field label="Description" value={label} onChange={setLabel} placeholder="Registration desk" wide />
          <label className="flex items-center gap-1.5 text-xs text-slate-300 h-8 px-2">
            <input
              type="checkbox"
              checked={isAdmin}
              onChange={(e) => setIsAdmin(e.target.checked)}
              className="w-3.5 h-3.5"
            />
            Reaches the admin area
          </label>
          <button
            onClick={create}
            disabled={busy || !name.trim() || !label.trim()}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium bg-blue-500/20 text-blue-200 border border-blue-500/30 hover:bg-blue-500/30 transition-colors disabled:opacity-40"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Create
          </button>
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        {roles.map((role) => (
          <div key={role.id} className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs px-2 py-0.5 rounded-md bg-slate-700/50 border border-slate-600/40">
                    {role.name}
                  </span>
                  {role.is_admin && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-200 border border-blue-500/30">
                      ADMIN AREA
                    </span>
                  )}
                  {role.protected && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-600/30 text-slate-300 border border-slate-500/30">
                      BUILT IN
                    </span>
                  )}
                </div>
                <div className="text-sm mt-1">{role.label}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">
                  {role.users} user{role.users === 1 ? "" : "s"} ·{" "}
                  {role.permissions.length} permission{role.permissions.length === 1 ? "" : "s"}
                </div>
              </div>
              {!role.protected && (
                <button
                  onClick={() => {
                    if (window.confirm(`Delete the role “${role.name}”?`)) {
                      mutate("delete_role", { id: role.id }, `Role “${role.name}” deleted.`);
                    }
                  }}
                  title="Delete this role"
                  className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-red-500/15 text-red-300 border border-red-500/25 hover:bg-red-500/25 transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="flex flex-wrap gap-1">
              {role.permissions.length === 0 ? (
                <span className="text-[11px] text-slate-500">
                  No permissions yet — grant them on the Permissions tab.
                </span>
              ) : (
                role.permissions.map((p) => (
                  <span
                    key={p}
                    className="font-mono text-[10px] px-1.5 py-0.5 rounded-md bg-emerald-500/10 text-emerald-300 border border-emerald-500/25"
                  >
                    {p}
                  </span>
                ))
              )}
            </div>
          </div>
        ))}
      </div>

      <UserAssignment roles={roles} permissions={permissions} mutate={mutate} />
    </div>
  );
};

/** Search a user, see the role they carry and the extras granted to them personally. */
const UserAssignment = ({
  roles,
  permissions,
  mutate,
}: {
  roles: Role[];
  permissions: PermissionRow[];
  mutate: (path: string, payload: any, success: string) => Promise<boolean>;
}) => {
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);

  const load = async (term: string) => {
    setLoading(true);
    try {
      const response = await axios.get(api("role_users"), { params: { search: term } });
      setUsers(response.data || []);
    } catch (e) {
      console.error("Error loading users:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load("");
  }, []);

  const rolePermissions = useMemo(
    () => new Map(roles.map((r) => [r.name, new Set(r.permissions)])),
    [roles]
  );

  return (
    <div className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-3 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs font-semibold text-slate-300">Who has which role</div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load(search);
          }}
          className="flex items-center gap-1.5"
        >
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or email…"
              className="h-8 pl-7 pr-2 w-64 rounded-md bg-slate-900/60 border border-slate-700/60 text-xs text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-blue-500/50"
            />
          </div>
          <button
            type="submit"
            className="h-8 px-3 rounded-md text-xs bg-slate-700/40 text-slate-200 border border-slate-600/40 hover:bg-slate-700/60 transition-colors"
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                load("");
              }}
              className="h-8 px-2 rounded-md text-xs text-slate-400 hover:text-slate-200"
            >
              Clear
            </button>
          )}
        </form>
      </div>

      <p className="text-[11px] text-slate-500">
        {search
          ? `Search results for “${search}” (100 max).`
          : "Everyone whose role is not the plain delegate default. Search to find anyone else."}
      </p>

      {loading ? (
        <div className="text-xs text-slate-400">Loading…</div>
      ) : users.length === 0 ? (
        <div className="text-xs text-slate-500">No users found.</div>
      ) : (
        <div className="divide-y divide-slate-700/40">
          {users.map((user) => {
            const granted = rolePermissions.get(user.role) || new Set<string>();
            return (
              <div key={user.id} className="py-2 space-y-1.5">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-sm truncate">{user.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{user.email}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {user.direct_permissions.length > 0 && (
                      <button
                        onClick={() => setExpanded(expanded === user.id ? null : user.id)}
                        className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-200 border border-amber-500/30"
                        title="Permissions granted to this person directly, on top of their role"
                      >
                        +{user.direct_permissions.length} personal
                      </button>
                    )}
                    <select
                      value={user.role}
                      onChange={(e) =>
                        mutate(
                          "assign_user_role",
                          { user_id: user.id, role: e.target.value },
                          `${user.email} is now ${e.target.value}.`
                        ).then((ok) => {
                            if (ok) load(search);
                          })
                      }
                      className="h-8 px-2 rounded-md bg-slate-900/60 border border-slate-700/60 text-xs text-slate-100 focus:outline-none focus:border-blue-500/50"
                    >
                      {roles.map((role) => (
                        <option key={role.id} value={role.name}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {expanded === user.id && (
                  <div className="flex flex-wrap gap-1 pl-1">
                    {user.direct_permissions.map((p) => (
                      <button
                        key={p}
                        onClick={() =>
                          mutate(
                            "set_user_permission",
                            { user_id: user.id, permission: p, value: false },
                            `Removed ${p} from ${user.email}.`
                          ).then((ok) => {
                            if (ok) load(search);
                          })
                        }
                        title={
                          granted.has(p)
                            ? "Also granted by their role — removing this changes nothing"
                            : "Granted to this person only. Click to remove."
                        }
                        className={`font-mono text-[10px] px-1.5 py-0.5 rounded-md border inline-flex items-center gap-1 ${
                          granted.has(p)
                            ? "bg-slate-700/30 text-slate-400 border-slate-600/40"
                            : "bg-amber-500/10 text-amber-200 border-amber-500/25 hover:bg-amber-500/20"
                        }`}
                      >
                        {p}
                        <X className="h-2.5 w-2.5" />
                      </button>
                    ))}
                    {permissions.length > user.direct_permissions.length && (
                      <select
                        value=""
                        onChange={(e) =>
                          e.target.value &&
                          mutate(
                            "set_user_permission",
                            { user_id: user.id, permission: e.target.value, value: true },
                            `Granted ${e.target.value} to ${user.email}.`
                          ).then((ok) => {
                            if (ok) load(search);
                          })
                        }
                        className="h-6 px-1 rounded-md bg-slate-900/60 border border-slate-700/60 text-[10px] text-slate-300"
                      >
                        <option value="">+ personal permission…</option>
                        {permissions
                          .filter((p) => !user.direct_permissions.includes(p.name))
                          .map((p) => (
                            <option key={p.id} value={p.name}>
                              {p.name}
                            </option>
                          ))}
                      </select>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------- permissions

const PermissionsTab = ({
  roles,
  permissions,
  mutate,
}: {
  roles: Role[];
  permissions: PermissionRow[];
  mutate: (path: string, payload: any, success: string) => Promise<boolean>;
}) => {
  const [name, setName] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);

  const create = async () => {
    setBusy(true);
    const ok = await mutate("save_permission", { name, label }, `Permission “${name}” saved.`);
    if (ok) {
      setName("");
      setLabel("");
    }
    setBusy(false);
  };

  /** Toggling a cell replaces that role's whole set — the endpoint is a sync. */
  const toggle = async (role: Role, permission: string) => {
    const next = role.permissions.includes(permission)
      ? role.permissions.filter((p) => p !== permission)
      : [...role.permissions, permission];

    setSaving(`${role.id}:${permission}`);
    await mutate(
      "set_role_permissions",
      { id: role.id, permissions: next },
      `${role.name}: ${next.includes(permission) ? "granted" : "revoked"} ${permission}.`
    );
    setSaving(null);
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-400 max-w-3xl">
        Tick a cell to grant a permission to a role. A user’s access is their role’s
        permissions <strong>plus</strong> anything granted to them personally on the Roles
        tab, so removing a tick here does not necessarily remove someone’s access.
      </p>

      <div className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-3">
        <div className="text-xs font-semibold text-slate-300 mb-2">New permission</div>
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Name (slug)" value={name} onChange={setName} placeholder="ijcai_something" mono />
          <Field label="Description" value={label} onChange={setLabel} placeholder="What it unlocks" wide />
          <button
            onClick={create}
            disabled={busy || !name.trim() || !label.trim()}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium bg-blue-500/20 text-blue-200 border border-blue-500/30 hover:bg-blue-500/30 transition-colors disabled:opacity-40"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Create
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          A new permission does nothing until the code checks for it by name — creating one
          here does not create a screen.
        </p>
      </div>

      <div className="rounded-xl border border-slate-700/50 bg-slate-800/40 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-900/60">
              <th className="text-left font-semibold text-slate-300 px-3 py-2 sticky left-0 bg-slate-900/95">
                Permission
              </th>
              {roles.map((role) => (
                <th key={role.id} className="px-2 py-2 font-semibold text-slate-300 whitespace-nowrap">
                  <div className="font-mono text-[11px]">{role.name}</div>
                  {!role.is_admin && (
                    <div className="text-[9px] font-normal text-slate-500">no admin area</div>
                  )}
                </th>
              ))}
              <th className="px-2 py-2 font-semibold text-slate-300 whitespace-nowrap">Personal</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {permissions.map((permission) => (
              <tr key={permission.id} className="border-t border-slate-700/40">
                <td className="px-3 py-1.5 sticky left-0 bg-slate-800/95">
                  <div className="font-mono text-[11px] text-slate-100">{permission.name}</div>
                  <div className="text-[10px] text-slate-500">{permission.label}</div>
                  {permission.opens && (
                    <div className="text-[10px] text-teal-400/80 mt-0.5">
                      opens the {permission.opens} tab
                    </div>
                  )}
                </td>
                {roles.map((role) => {
                  const on = role.permissions.includes(permission.name);
                  const key = `${role.id}:${permission.name}`;
                  return (
                    <td key={role.id} className="px-2 py-1.5 text-center">
                      <button
                        onClick={() => toggle(role, permission.name)}
                        disabled={saving === key}
                        title={`${on ? "Revoke" : "Grant"} ${permission.name} for ${role.name}`}
                        className={`inline-flex items-center justify-center h-6 w-6 rounded-md border transition-colors ${
                          on
                            ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/30"
                            : "bg-slate-700/20 text-slate-600 border-slate-600/40 hover:border-slate-500/60"
                        } disabled:opacity-40`}
                      >
                        {saving === key ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : on ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : (
                          <span className="text-[10px]">·</span>
                        )}
                      </button>
                    </td>
                  );
                })}
                <td className="px-2 py-1.5 text-center text-[11px] text-slate-400">
                  {permission.direct_users || "—"}
                </td>
                <td className="px-2 py-1.5 text-right">
                  {permission.name !== "ijcai_permissions" && (
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            `Delete “${permission.name}”? It will be removed from every role and from ${permission.direct_users} personal grant(s).`
                          )
                        ) {
                          mutate(
                            "delete_permission",
                            { id: permission.id },
                            `Permission “${permission.name}” deleted.`
                          );
                        }
                      }}
                      title="Delete this permission"
                      className="inline-flex items-center justify-center h-6 w-6 rounded-md text-slate-500 hover:text-red-300 hover:bg-red-500/15 transition-colors"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-start gap-2 text-[11px] text-slate-500">
        <KeyRound className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <span>
          The <code>admin</code> role passes every permission check regardless of what is
          ticked here — otherwise nobody could reach this screen to grant the first one.
        </span>
      </div>
    </div>
  );
};

const Field = ({
  label,
  value,
  onChange,
  placeholder,
  mono,
  wide,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
  wide?: boolean;
}) => (
  <label className="flex flex-col gap-1">
    <span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`h-8 px-2 rounded-md bg-slate-900/60 border border-slate-700/60 text-xs text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-blue-500/50 ${
        mono ? "font-mono" : ""
      } ${wide ? "w-64" : "w-44"}`}
    />
  </label>
);

export default Permissions;
